import { ArrowLeft, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

// Curated changelog. Add new entries at the top.
// Keep entries customer-facing — no implementation details.
const ENTRIES = [
  {
    date: 'Feb 2026',
    type: 'feature',
    title: 'Live transcripts now feel instant',
    body: 'Switched to Server-Sent Events for sub-second transcript delivery during meetings. You\'ll see what the prospect just said in well under a second.',
  },
  {
    date: 'Feb 2026',
    type: 'feature',
    title: 'Pin a question',
    body: 'On both the main dashboard and the PiP coach, you can now click the 📌 icon on any AI question to keep it at the top of your suggestions list — survives analysis refreshes so you never lose a question you wanted to ask.',
  },
  {
    date: 'Feb 2026',
    type: 'feature',
    title: 'Coaching highlights strip',
    body: 'When you actually ask a question the AI suggested, it lights up in a green highlights strip above your transcript. Reward for following the coach, plus a metric your manager will love.',
  },
  {
    date: 'Feb 2026',
    type: 'feature',
    title: 'Microsoft Teams support',
    body: 'PipeGuide now joins Microsoft Teams meetings natively (alongside Google Meet and Zoom). Just paste the share link.',
  },
  {
    date: 'Feb 2026',
    type: 'design',
    title: 'New PipeGuide brand colors',
    body: 'Refreshed visual identity — deep petrol teal primary with an aqua mint accent and a Guidance Gold signature for pinned items and premium actions.',
  },
  {
    date: 'Feb 2026',
    type: 'improvement',
    title: 'Better error messages when a bot can\'t join',
    body: 'If a bot fails to join (bad link, password required, meeting full, etc.) you\'ll now see a plain-English explanation and a one-click "Try a different link" button instead of a cryptic technical error.',
  },
  {
    date: 'Feb 2026',
    type: 'improvement',
    title: 'Top up tokens from the Platform Dashboard',
    body: 'Platform admins can now credit tokens to any org\'s overage pool directly from the Organizations tab. Useful for EFT customers and demo refills.',
  },
  {
    date: 'Feb 2026',
    type: 'improvement',
    title: '15-second AI scoring is bulletproof',
    body: 'Fixed an edge case where the analyzer countdown could restart mid-call. Scores and suggested questions now refresh every 15 seconds, every time.',
  },
];

const TYPE_STYLES = {
  feature: { label: 'New', bg: '#E6F3F5', color: '#0E4E5A' },
  improvement: { label: 'Better', bg: '#E8F8F5', color: '#0E4E5A' },
  fix: { label: 'Fixed', bg: '#ECFDF5', color: '#065F46' },
  design: { label: 'Refresh', bg: '#FEF5E2', color: '#92590F' },
};

export default function ChangelogPage() {
  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-brand-primary text-white px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/" className="text-sm opacity-80 hover:opacity-100 flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to app
          </Link>
          <div className="text-sm font-bold tracking-wide">What's new</div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <div className="mb-8">
          <div className="flex items-center gap-2 text-brand-primary mb-2">
            <Sparkles className="w-5 h-5" />
            <span className="text-xs font-bold uppercase tracking-[0.15em]">Product updates</span>
          </div>
          <h1 className="text-3xl font-bold text-zinc-900" style={{ fontFamily: 'var(--font-heading)' }}>
            Built with you, shipped fast
          </h1>
          <p className="text-zinc-600 mt-2">
            Everything we've shipped recently. Refresh this page anytime — we update it as we ship.
          </p>
        </div>

        <div className="space-y-5" data-testid="changelog-entries">
          {ENTRIES.map((e, i) => {
            const style = TYPE_STYLES[e.type] || TYPE_STYLES.improvement;
            return (
              <article
                key={i}
                className="bg-white border border-zinc-200 rounded-xl p-5"
                data-testid={`changelog-entry-${i}`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <span
                    className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full"
                    style={{ background: style.bg, color: style.color }}
                  >
                    {style.label}
                  </span>
                  <span className="text-xs text-zinc-500">{e.date}</span>
                </div>
                <h3 className="font-bold text-zinc-900 mb-1">{e.title}</h3>
                <p className="text-sm text-zinc-700 leading-relaxed">{e.body}</p>
              </article>
            );
          })}
        </div>

        <div className="mt-10 text-center text-sm text-zinc-500">
          Got an idea or feedback?{' '}
          <a href="mailto:support@pipeguide.tech" className="text-brand-primary font-semibold">
            Email us
          </a>{' '}
          — we read every message.
        </div>
      </main>
    </div>
  );
}
